export type ReportingPermission = 'report.view.history' | 'report.export';

export type ReportingAuthorize = (request: {
  readonly actorId: string;
  readonly permission: ReportingPermission;
  readonly at: string;
}) => void;

export interface HistoricalQueryInput<T> {
  readonly actorId: string;
  readonly at: string;
  readonly authorize: ReportingAuthorize;
  readonly build: () => T;
}

export interface ReportExportInput<T extends { readonly mode: string; readonly historicalMode?: string }> {
  readonly actorId: string;
  readonly at: string;
  readonly authorize: ReportingAuthorize;
  readonly generatedAt: string;
  readonly report: T;
  readonly filters: Readonly<Record<string, unknown>>;
  readonly sourceIdentity: string;
  readonly freshness: 'fresh' | 'stale';
}

export interface ReportExportProjection<T> {
  readonly generatedAt: string;
  readonly mode: string;
  readonly historicalMode: string | null;
  readonly filters: Readonly<Record<string, unknown>>;
  readonly sourceIdentity: string;
  readonly freshness: 'fresh' | 'stale';
  readonly report: T;
}

const clone = <T>(value: T): T => structuredClone(value);

const assertTimestamp = (value: string, code: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error(code);
};

export function queryHistoricalReport<T>(input: HistoricalQueryInput<T>): T {
  assertTimestamp(input.at, 'REPORTING_QUERY_TIME_INVALID');
  input.authorize({ actorId: input.actorId, permission: 'report.view.history', at: input.at });
  return clone(input.build());
}

export function buildReportExport<T extends { readonly mode: string; readonly historicalMode?: string }>(
  input: ReportExportInput<T>,
): ReportExportProjection<T> {
  assertTimestamp(input.at, 'REPORTING_QUERY_TIME_INVALID');
  assertTimestamp(input.generatedAt, 'REPORTING_EXPORT_GENERATED_AT_INVALID');
  if (!input.sourceIdentity.trim()) throw new Error('REPORTING_EXPORT_SOURCE_IDENTITY_REQUIRED');
  input.authorize({ actorId: input.actorId, permission: 'report.export', at: input.at });
  return {
    generatedAt: input.generatedAt,
    mode: input.report.mode,
    historicalMode: input.report.historicalMode ?? null,
    filters: clone(input.filters),
    sourceIdentity: input.sourceIdentity,
    freshness: input.freshness,
    report: clone(input.report),
  };
}
